/**
 * Maps a Route (plus the resolved plugin, when on a detail/edit page) to a
 * human-meaningful document title. Kept separate from App.tsx so the mapping is
 * unit-testable without the full component/auth/env dependency graph, mirroring
 * routing.ts. Distinct titles let Google Analytics distinguish page visits that
 * previously all reported the static "AI Marketplace" title.
 */

import type { Route } from "./routing";
import type { MarketplaceEntry } from "../types/index";

export const BASE_TITLE = "AI Marketplace";

/**
 * The view-specific name, without the constant "AI Marketplace - " product
 * prefix. Used for the route-change screen-reader announcement so it leads with
 * what changed (the view) instead of repeating the product name on every
 * navigation. The catalog — whose document title is just the product name —
 * announces "Marketplace" (matching the breadcrumb root).
 */
export function resolveViewName(route: Route, plugin?: MarketplaceEntry | null): string {
  switch (route.page) {
    case "catalog":
      return "Marketplace";
    case "detail":
      return plugin ? plugin.pluginName : "Not Found";
    case "edit":
      return plugin ? `Edit ${plugin.pluginName}` : "Edit";
    case "contribute":
      return "Contribute a Skill";
    case "help":
      return "Help";
    case "admin":
      return "Admin";
    case "myskills":
      return "My Skills";
    case "mcp":
      return "MCP Servers";
    case "mcpserver":
      return `MCP Servers - ${route.serverName}`;
    case "connections":
      return "My Connections";
    default: {
      // Exhaustiveness guard: a new Route variant must add a case above, or this
      // fails to compile (tsconfig has no noImplicitReturns to catch it otherwise).
      const _exhaustive: never = route;
      return _exhaustive;
    }
  }
}

export function resolvePageTitle(route: Route, plugin?: MarketplaceEntry | null): string {
  // The catalog's title is just the product name; every other view prefixes it.
  return route.page === "catalog"
    ? BASE_TITLE
    : `${BASE_TITLE} - ${resolveViewName(route, plugin)}`;
}
